import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as SkillIcon,
  Close as CloseIcon,
  ContentCopy as CloneIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Link as AssignIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { agentApi } from '../../../services/api';
import type {
  AgentSkill,
  AgentSkillAssignment,
  AgentSkillCreate,
  AgentSkillUpdate,
  SkillActivationMode,
} from '../../../types';
import { SectionCard } from './primitives';

interface SkillsTabProps {
  agentId: number;
}

interface SkillFormState {
  name: string;
  description: string;
  category: string;
  tags: string;
  instructions: string;
  triggerDescription: string;
  triggerKeywords: string;
  requiredTools: string;
  optionalTools: string;
  resources: string;
  configSchema: string;
  tests: string;
  isActive: boolean;
  isPublic: boolean;
}

const EMPTY_FORM: SkillFormState = {
  name: '',
  description: '',
  category: '',
  tags: '',
  instructions: '',
  triggerDescription: '',
  triggerKeywords: '',
  requiredTools: '',
  optionalTools: '',
  resources: '[]',
  configSchema: '{}',
  tests: '[]',
  isActive: true,
  isPublic: false,
};

const ACTIVATION_OPTIONS: Array<{
  value: SkillActivationMode;
  label: string;
  description: string;
}> = [
  {
    value: 'AUTO',
    label: 'Auto',
    description: 'Activate when the message matches the skill triggers.',
  },
  {
    value: 'MANUAL',
    label: 'Manual',
    description: 'Activate only when explicitly requested by id, slug, or chat directive.',
  },
  {
    value: 'ALWAYS',
    label: 'Always',
    description: 'Load the skill for every execution of this agent.',
  },
];

const csv = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const jsonValue = <T,>(value: string, fallback: T): T => {
  const trimmed = value.trim();
  return trimmed ? (JSON.parse(trimmed) as T) : fallback;
};

const firstErrorMessage = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = firstErrorMessage(item);
      if (message) return message;
    }
  }
  if (value && typeof value === 'object') {
    for (const item of Object.values(value)) {
      const message = firstErrorMessage(item);
      if (message) return message;
    }
  }
  return null;
};

const errorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  return firstErrorMessage(data?.detail) || firstErrorMessage(data?.error) ||
    firstErrorMessage(data) || fallback;
};

const formFromSkill = (skill: AgentSkill): SkillFormState => {
  const version = skill.current_version;
  return {
    name: skill.name,
    description: skill.description || '',
    category: skill.category || '',
    tags: (skill.tags || []).join(', '),
    instructions: version?.instructions || '',
    triggerDescription: version?.trigger_description || '',
    triggerKeywords: (version?.trigger_keywords || []).join(', '),
    requiredTools: (version?.required_tools || []).join(', '),
    optionalTools: (version?.optional_tools || []).join(', '),
    resources: JSON.stringify(version?.resources || [], null, 2),
    configSchema: JSON.stringify(version?.config_schema || {}, null, 2),
    tests: JSON.stringify(version?.tests || [], null, 2),
    isActive: skill.is_active,
    isPublic: skill.is_public,
  };
};

const SkillSummary: React.FC<{ skill: AgentSkill; versionLabel?: string }> = ({
  skill,
  versionLabel,
}) => {
  const version = skill.current_version;
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {skill.category ? <Chip size="small" label={skill.category} /> : null}
        <Chip
          size="small"
          variant="outlined"
          label={versionLabel || `v${version?.version || '—'}`}
        />
        {skill.is_template ? (
          <Chip size="small" color="info" variant="outlined" label="Template" />
        ) : null}
        {skill.is_public ? (
          <Chip size="small" color="success" variant="outlined" label="Public" />
        ) : null}
        {(skill.tags || []).slice(0, 4).map((tag) => (
          <Chip key={tag} size="small" variant="outlined" label={tag} />
        ))}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {skill.description || version?.trigger_description || 'No description provided.'}
      </Typography>
      {(version?.trigger_keywords || []).length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          Triggers: {version?.trigger_keywords.join(', ')}
        </Typography>
      ) : null}
      {(version?.required_tools || []).length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          Required tools: {version?.required_tools.join(', ')}
        </Typography>
      ) : null}
    </Stack>
  );
};

export const SkillsTab: React.FC<SkillsTabProps> = ({ agentId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<AgentSkill | null>(null);
  const [form, setForm] = useState<SkillFormState>(EMPTY_FORM);
  const [configAssignment, setConfigAssignment] =
    useState<AgentSkillAssignment | null>(null);
  const [assignmentConfig, setAssignmentConfig] = useState('{}');
  const [assignmentPriority, setAssignmentPriority] = useState(0);

  const { data, isLoading, error } = useQuery({
    queryKey: ['agent-skills', agentId],
    queryFn: () => agentApi.getSkills(agentId),
    enabled: !!agentId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['agent-skills', agentId] });
    queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    queryClient.invalidateQueries({ queryKey: ['agent-skills'] });
  };

  const assignMutation = useMutation({
    mutationFn: (skillId: number) =>
      agentApi.assignSkill(agentId, {
        skill_id: skillId,
        activation_mode: 'AUTO',
        is_enabled: true,
      }),
    onSuccess: () => {
      invalidate();
      toast.success('Skill assigned');
    },
    onError: (err: any) => toast.error(errorMessage(err, 'Failed to assign skill')),
  });

  const updateAssignmentMutation = useMutation({
    mutationFn: ({
      assignmentId,
      payload,
    }: {
      assignmentId: number;
      payload: Partial<AgentSkillAssignment> & { pinned_version_id?: number | null };
    }) => agentApi.updateSkillAssignment(agentId, assignmentId, payload),
    onSuccess: invalidate,
    onError: (err: any) =>
      toast.error(errorMessage(err, 'Failed to update skill assignment')),
  });

  const unassignMutation = useMutation({
    mutationFn: (skillId: number) => agentApi.unassignSkill(agentId, skillId),
    onSuccess: () => {
      invalidate();
      toast.success('Skill removed from agent');
    },
    onError: (err: any) => toast.error(errorMessage(err, 'Failed to remove skill')),
  });

  const cloneAndAssignMutation = useMutation({
    mutationFn: async (skillId: number) => {
      const clonedSkill = await agentApi.cloneSkill(skillId);
      await agentApi.assignSkill(agentId, {
        skill_id: clonedSkill.id,
        activation_mode: 'AUTO',
        is_enabled: true,
      });
      return clonedSkill;
    },
    onSuccess: () => {
      invalidate();
      setTemplateDialogOpen(false);
      toast.success('Public skill copied and assigned');
    },
    onError: (err: any) =>
      toast.error(errorMessage(err, 'Failed to copy public skill')),
  });

  const saveSkillMutation = useMutation({
    mutationFn: (payload: AgentSkillCreate | AgentSkillUpdate) =>
      editingSkill
        ? agentApi.updateSkill(editingSkill.id, payload as AgentSkillUpdate)
        : agentApi.createSkill(payload as AgentSkillCreate),
    onSuccess: async (skill) => {
      invalidate();
      setDialogOpen(false);
      setEditingSkill(null);
      setForm(EMPTY_FORM);
      if (!editingSkill) {
        await assignMutation.mutateAsync(skill.id);
      } else {
        toast.success('New skill version saved');
      }
    },
    onError: (err: any) => toast.error(errorMessage(err, 'Failed to save skill')),
  });

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matches = (skill: AgentSkill) =>
      !query ||
      [skill.name, skill.slug, skill.description, skill.category, ...(skill.tags || [])]
        .join(' ')
        .toLowerCase()
        .includes(query);
    return {
      assigned: (data?.assigned || []).filter((item) => matches(item.skill_details)),
      available: (data?.available || []).filter(matches),
      publicTemplates: (data?.public_templates || []).filter(matches),
    };
  }, [data, search]);

  const openCreate = () => {
    setEditingSkill(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (skill: AgentSkill) => {
    setEditingSkill(skill);
    setForm(formFromSkill(skill));
    setDialogOpen(true);
  };

  const saveSkill = () => {
    if (!form.name.trim() || !form.instructions.trim()) {
      toast.error('Name and instructions are required');
      return;
    }
    try {
      const payload: AgentSkillCreate = {
        name: form.name.trim(),
        description: form.description.trim(),
        category: form.category.trim(),
        tags: csv(form.tags),
        is_active: form.isActive,
        is_public: form.isPublic,
        instructions: form.instructions.trim(),
        trigger_description: form.triggerDescription.trim(),
        trigger_keywords: csv(form.triggerKeywords),
        required_tools: csv(form.requiredTools),
        optional_tools: csv(form.optionalTools),
        resources: jsonValue(form.resources, []),
        config_schema: jsonValue(form.configSchema, {}),
        tests: jsonValue(form.tests, []),
      };
      saveSkillMutation.mutate(payload);
    } catch (err: any) {
      toast.error(`Invalid JSON: ${err?.message || 'check advanced fields'}`);
    }
  };

  const openAssignmentConfig = (assignment: AgentSkillAssignment) => {
    setConfigAssignment(assignment);
    setAssignmentConfig(JSON.stringify(assignment.custom_config || {}, null, 2));
    setAssignmentPriority(assignment.priority || 0);
  };

  const saveAssignmentConfig = async () => {
    if (!configAssignment) return;
    try {
      const customConfig = jsonValue<Record<string, any>>(assignmentConfig, {});
      if (!customConfig || Array.isArray(customConfig) || typeof customConfig !== 'object') {
        toast.error('Assignment configuration must be a JSON object');
        return;
      }
      await updateAssignmentMutation.mutateAsync({
        assignmentId: configAssignment.id,
        payload: {
          custom_config: customConfig,
          priority: assignmentPriority,
        },
      });
      setConfigAssignment(null);
      toast.success('Skill assignment settings saved');
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        toast.error(`Invalid configuration JSON: ${err.message}`);
      }
    }
  };

  if (isLoading) {
    return <Typography color="text.secondary">Loading skills…</Typography>;
  }

  if (error) {
    return <Alert severity="error">Unable to load skills for this agent.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <SectionCard
        title="Agent skills"
        icon={<SkillIcon fontSize="small" />}
        description="Skills are reusable, versioned instruction bundles. They can narrow the agent's assigned tools, but never grant new tool access."
        actions={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloneIcon />}
              onClick={() => setTemplateDialogOpen(true)}
            >
              Create from template
            </Button>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New blank skill
            </Button>
          </Stack>
        }
      >
        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search skills, categories, or triggers"
          slotProps={{ input: { startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} /> } }}
        />
      </SectionCard>

      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Assigned ({data?.assigned.length || 0})
        </Typography>
        {filtered.assigned.length === 0 ? (
          <Alert severity="info">No matching skills are assigned to this agent.</Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            {filtered.assigned.map((assignment) => {
              const skill = assignment.skill_details;
              const effective = assignment.effective_version;
              return (
                <Paper
                  key={assignment.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderLeft: '3px solid',
                    borderColor: assignment.is_enabled ? 'primary.main' : 'divider',
                    bgcolor: assignment.is_enabled
                      ? alpha(theme.palette.primary.main, 0.025)
                      : 'background.paper',
                  }}
                >
                  <Stack spacing={1.25}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap>
                          {skill.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {skill.slug}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.25}>
                        <Tooltip title="Assignment configuration and priority">
                          <IconButton
                            size="small"
                            onClick={() => openAssignmentConfig(assignment)}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {skill.can_edit ? (
                          <Tooltip title="Edit metadata and create a new immutable version">
                            <IconButton size="small" onClick={() => openEdit(skill)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        <Tooltip title="Remove from agent">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => unassignMutation.mutate(skill.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    <SkillSummary
                      skill={{ ...skill, current_version: effective || skill.current_version }}
                      versionLabel={`v${effective?.version || '—'}${assignment.pinned_version ? ' pinned' : ''}`}
                    />

                    {assignment.missing_required_tools.length > 0 ? (
                      <Alert severity="warning" sx={{ py: 0.25 }}>
                        Missing required tools: {assignment.missing_required_tools.join(', ')}
                      </Alert>
                    ) : null}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id={`skill-activation-label-${assignment.id}`}>
                          Activation
                        </InputLabel>
                        <Select
                          id={`skill-activation-${assignment.id}`}
                          labelId={`skill-activation-label-${assignment.id}`}
                          value={assignment.activation_mode}
                          label="Activation"
                          onChange={(event) =>
                            updateAssignmentMutation.mutate({
                              assignmentId: assignment.id,
                              payload: { activation_mode: event.target.value as SkillActivationMode },
                            })
                          }
                        >
                          {ACTIVATION_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Box>
                                <Typography variant="body2">{option.label}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.description}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth>
                        <InputLabel id={`skill-version-label-${assignment.id}`}>
                          Version
                        </InputLabel>
                        <Select
                          id={`skill-version-${assignment.id}`}
                          labelId={`skill-version-label-${assignment.id}`}
                          value={assignment.pinned_version || ''}
                          label="Version"
                          onChange={(event) =>
                            updateAssignmentMutation.mutate({
                              assignmentId: assignment.id,
                              payload: {
                                pinned_version_id: event.target.value
                                  ? Number(event.target.value)
                                  : null,
                              },
                            })
                          }
                        >
                          <MenuItem value="">Latest published</MenuItem>
                          {[...(skill.versions || [])]
                            .filter((version) => version.is_published)
                            .sort((a, b) => b.version - a.version)
                            .map((version) => (
                              <MenuItem key={version.id} value={version.id}>
                                Version {version.version}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Stack>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={assignment.is_enabled}
                          onChange={(event) =>
                            updateAssignmentMutation.mutate({
                              assignmentId: assignment.id,
                              payload: { is_enabled: event.target.checked },
                            })
                          }
                        />
                      }
                      label={assignment.is_enabled ? 'Enabled' : 'Disabled'}
                    />
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Public skill templates ({data?.public_templates?.length || 0})
        </Typography>
        {filtered.publicTemplates.length === 0 ? (
          <Alert severity="info">No matching public skill templates are available.</Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            {filtered.publicTemplates.map((skill) => (
              <Paper key={skill.id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.25}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      {skill.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      by {skill.owner_name} · {skill.slug}
                    </Typography>
                  </Box>
                  <SkillSummary skill={skill} />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CloneIcon />}
                    onClick={() => cloneAndAssignMutation.mutate(skill.id)}
                    disabled={!skill.can_clone || cloneAndAssignMutation.isPending}
                  >
                    Create private copy and assign
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Available ({data?.available.length || 0})
        </Typography>
        {filtered.available.length === 0 ? (
          <Alert severity="info">No additional matching skills are available.</Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            {filtered.available.map((skill) => (
              <Paper key={skill.id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.25}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {skill.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {skill.slug}
                      </Typography>
                    </Box>
                    {skill.can_edit ? (
                      <IconButton size="small" onClick={() => openEdit(skill)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Stack>
                  <SkillSummary skill={skill} />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AssignIcon />}
                    onClick={() => assignMutation.mutate(skill.id)}
                    disabled={assignMutation.isPending}
                  >
                    Assign to agent
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontWeight={700}>Create from skill template</Typography>
              <Typography variant="body2" color="text.secondary">
                This creates a private copy and assigns it to this agent.
              </Typography>
            </Box>
            <IconButton onClick={() => setTemplateDialogOpen(false)}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {(data?.public_templates || []).length === 0 ? (
            <Alert
              severity="info"
              action={<Button color="inherit" size="small" href="/ai/skills">Manage skills</Button>}
            >
              No public templates are available. Confirm migrations 0056 and 0057 completed,
              or publish a skill from the Skills library.
            </Alert>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.5,
              }}
            >
              {(data?.public_templates || []).map((skill) => (
                <Paper key={skill.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.25}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>{skill.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {skill.owner_name} · {skill.slug}
                      </Typography>
                    </Box>
                    <SkillSummary skill={skill} />
                    <Button
                      variant="contained"
                      startIcon={<CloneIcon />}
                      onClick={() => cloneAndAssignMutation.mutate(skill.id)}
                      disabled={!skill.can_clone || cloneAndAssignMutation.isPending}
                    >
                      Create copy and assign
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button href="/ai/skills">Open Skills library</Button>
          <Button onClick={() => setTemplateDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {editingSkill ? `Edit ${editingSkill.name}` : 'Create agent skill'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editingSkill
                  ? 'Saving instruction changes creates a new immutable version.'
                  : 'The new skill will be assigned to this agent after creation.'}
              </Typography>
            </Box>
            <IconButton onClick={() => setDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                required
                fullWidth
                label="Name"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
              <TextField
                fullWidth
                label="Category"
                value={form.category}
                onChange={(event) => setForm({ ...form, category: event.target.value })}
              />
            </Stack>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
            <TextField
              fullWidth
              label="Tags"
              helperText="Comma-separated"
              value={form.tags}
              onChange={(event) => setForm({ ...form, tags: event.target.value })}
            />
            <TextField
              required
              fullWidth
              multiline
              minRows={8}
              label="Instructions"
              helperText="Loaded into the system prompt only when this skill activates."
              value={form.instructions}
              onChange={(event) => setForm({ ...form, instructions: event.target.value })}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Trigger description"
              value={form.triggerDescription}
              onChange={(event) =>
                setForm({ ...form, triggerDescription: event.target.value })
              }
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Trigger keywords"
                helperText="Comma-separated; used by deterministic AUTO activation"
                value={form.triggerKeywords}
                onChange={(event) =>
                  setForm({ ...form, triggerKeywords: event.target.value })
                }
              />
              <TextField
                fullWidth
                label="Required tools"
                helperText="Comma-separated registry names"
                value={form.requiredTools}
                onChange={(event) => setForm({ ...form, requiredTools: event.target.value })}
              />
              <TextField
                fullWidth
                label="Optional tools"
                helperText="Comma-separated registry names"
                value={form.optionalTools}
                onChange={(event) => setForm({ ...form, optionalTools: event.target.value })}
              />
            </Stack>
            <Alert severity="info">
              Tool names in a skill are intersected with tools already assigned to the agent.
              Skills cannot grant tool access.
            </Alert>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Resources (JSON)"
                value={form.resources}
                onChange={(event) => setForm({ ...form, resources: event.target.value })}
              />
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Configuration schema (JSON)"
                value={form.configSchema}
                onChange={(event) => setForm({ ...form, configSchema: event.target.value })}
              />
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Tests (JSON)"
                value={form.tests}
                onChange={(event) => setForm({ ...form, tests: event.target.value })}
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                />
              }
              label="Skill is active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isPublic}
                  onChange={(event) => setForm({ ...form, isPublic: event.target.checked })}
                />
              }
              label="Share as a public skill template"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Other users can discover and copy a public skill. Their copy is private and
              independent from future changes you make.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveSkill}
            disabled={saveSkillMutation.isPending || assignMutation.isPending}
          >
            {editingSkill ? 'Save new version' : 'Create and assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(configAssignment)}
        onClose={() => setConfigAssignment(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Assignment settings
          {configAssignment ? ` · ${configAssignment.skill_details.name}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              type="number"
              label="Priority"
              value={assignmentPriority}
              onChange={(event) => setAssignmentPriority(Number(event.target.value))}
              helperText="Higher-priority AUTO skills win when the activation limit is reached."
            />
            <TextField
              fullWidth
              multiline
              minRows={8}
              label="Custom configuration (JSON)"
              value={assignmentConfig}
              onChange={(event) => setAssignmentConfig(event.target.value)}
              helperText="Validated against the effective skill version's configuration schema."
            />
            {configAssignment?.effective_version?.config_schema &&
            Object.keys(configAssignment.effective_version.config_schema).length > 0 ? (
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                <Typography variant="caption" color="text.secondary">
                  Configuration schema
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    mt: 0.5,
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(
                    configAssignment.effective_version.config_schema,
                    null,
                    2
                  )}
                </Box>
              </Paper>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigAssignment(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveAssignmentConfig}
            disabled={updateAssignmentMutation.isPending}
          >
            Save settings
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SkillsTab;
